import { Group } from 'src/design/atoms/Group';
import { Select } from 'src/design/atoms/Select';
import { Stack } from 'src/design/atoms/Stack';
import { Textarea } from 'src/design/atoms/Textarea';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { ChatMemoryRequestDto } from 'src/api/FiretrolApiClient';
import {
  MEMORY_KIND_OPTIONS,
  MEMORY_KINDS,
  MEMORY_TEXT_MAX_LENGTH,
} from 'src/app/chat/memoryNotes';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import {
  applyValidationErrors,
  getErrorMessage,
  logger,
  showErrorToast,
} from 'src/utils';

export interface IMemoryNoteFormProps {
  initialValues?: ChatMemoryRequestDto;
  onSubmit: (values: ChatMemoryRequestDto) => Promise<unknown>;
  onCancel: () => void;
}

const validationSchema = Yup.object().shape({
  kind: Yup.string()
    .required('Kind is required')
    .oneOf(MEMORY_KINDS, `Kind must be one of: ${MEMORY_KINDS.join(', ')}`),
  text: Yup.string()
    .trim()
    .required('Text is required')
    .max(
      MEMORY_TEXT_MAX_LENGTH,
      `Text must be ${MEMORY_TEXT_MAX_LENGTH} characters or fewer`,
    ),
});

const EMPTY_VALUES: ChatMemoryRequestDto = { kind: 'preference', text: '' };

export const MemoryNoteForm: React.FC<IMemoryNoteFormProps> = ({
  initialValues = EMPTY_VALUES,
  onSubmit,
  onCancel,
}) => {
  const formik = useFormik<ChatMemoryRequestDto>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setFieldError }) => {
      try {
        await onSubmit({ kind: values.kind, text: values.text.trim() });
      } catch (err) {
        logger.error('Failed to save memory note', { err });
        const { applied, unmatchedMessages } = applyValidationErrors(
          err,
          Object.keys(values),
          setFieldError,
        );
        showErrorToast(
          applied && unmatchedMessages.length === 0
            ? 'Please fix the highlighted fields below.'
            : getErrorMessage(err),
        );
      }
    },
  });

  return (
    <Stack gap="sm">
      <Select
        label="Kind"
        withAsterisk
        name="kind"
        data={MEMORY_KIND_OPTIONS}
        allowDeselect={false}
        value={formik.values.kind}
        onChange={(value) => formik.setFieldValue('kind', value ?? '')}
        onBlur={() => formik.setFieldTouched('kind')}
        error={formik.touched.kind && formik.errors.kind}
      />
      <Textarea
        label="Note"
        withAsterisk
        name="text"
        description={`${
          formik.values.text.trim().length
        }/${MEMORY_TEXT_MAX_LENGTH} characters`}
        placeholder="Show labor as FTE, not hours."
        autosize
        minRows={2}
        value={formik.values.text}
        onChange={(event) =>
          formik.setFieldValue('text', event.currentTarget.value)
        }
        onBlur={formik.handleBlur}
        error={formik.touched.text && formik.errors.text}
      />
      <Group justify="flex-end" gap="sm">
        <SecondaryButton size="xs" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <SaveButton
          size="xs"
          loading={formik.isSubmitting}
          onClick={() => formik.handleSubmit()}
        >
          Save
        </SaveButton>
      </Group>
    </Stack>
  );
};
